// Project Sentinel - Customer Settings Context
// Provides customer configuration including fiscal year settings
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '../services/api'

interface CustomerSettings {
  fiscalYearStartMonth: number // 1-12
}

interface CustomerContextValue {
  settings: CustomerSettings | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

// Create context with undefined initial value (will be set by provider)
const CustomerContext = createContext<CustomerContextValue | undefined>(undefined)

// Provider props
type CustomerProviderProps = {
  children: React.ReactNode
}

// Provider component
export function CustomerProvider({ children }: CustomerProviderProps) {
  const [settings, setSettings] = useState<CustomerSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await api.getCustomerSettings()
      setSettings({
        fiscalYearStartMonth: data.fiscal_year_start_month
      })
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Unknown error'
      setError(errorMessage)
      console.error('Error fetching customer settings:', errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  // Settings belong to the signed-in account: reload them after a login, drop
  // them after a logout.
  useEffect(() => api.onSessionChange(() => {
    if (api.isAuthenticated()) {
      void fetchSettings()
    } else {
      setSettings(null)
    }
  }), [fetchSettings])

  const value = useMemo(
    () => ({
      settings,
      isLoading,
      error,
      refetch: fetchSettings,
    }),
    [settings, isLoading, error, fetchSettings]
  )

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  )
}

// Hook to use customer settings context
export function useCustomerSettings() {
  const context = useContext(CustomerContext)

  if (context === undefined) {
    throw new Error('useCustomerSettings must be used within a CustomerProvider')
  }

  return context
}

/** The customer settings when a CustomerProvider is above, else null (pages rendered without one, in tests). */
export function useOptionalCustomerSettings(): CustomerContextValue | null {
  return useContext(CustomerContext) ?? null
}
